import classNames from 'classnames'
import { Check, ChevronsUpDown } from 'lucide-react'
import { Dispatch, SetStateAction, useState } from 'react'

import { DetailSection, DefinitionList } from './detail-section'
import { formatDate } from './organization-header'

import { Segmented } from '@/components/activity/activity-panel'
import OrganizationHubspotCompany from '@/components/organization-hubspot-company'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { MultiSelectAutoComplete } from '@/components/ui/multi-select-autocomplete'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { IOrganization } from '@/models'

const SECTIONS = [
  { id: 'general', label: 'General' },
  { id: 'environment', label: 'Environment' },
  { id: 'tags', label: 'Tags' },
  { id: 'subscription', label: 'Subscription' },
  { id: 'hubspot', label: 'HubSpot' },
  { id: 'secure-proxy', label: 'Secure proxy' },
  { id: 'system', label: 'System' },
  { id: 'danger-zone', label: 'Danger zone' }
] as const

const sectionId = (id: string) => `org-settings-${id}`

/** no option is highlighted while the value isn't set; picking one sets it */
const toOptions = (values: string[]) =>
  values.map((value) => ({ value, label: value }))

type SettingsTabProps = {
  organization?: IOrganization
  draft: IOrganization
  onDraftChange: (patch: Partial<IOrganization>) => void
  tags: string[]
  setTags: Dispatch<SetStateAction<string[]>>
  allTags?: string[]
  isTagsLoading?: boolean
  productionOrganizations?: IOrganization[]
  elasticCluster?: string
  isSandbox: boolean
  disabled: boolean
  canDelete: boolean
  onOpenPricingTier: () => void
  onOpenHubspotData: () => void
  onOpenSecureProxyWhitelist: () => void
  onFactoryReset: () => void
  onDelete: () => void
}

export const SettingsTab = ({
  organization,
  draft,
  onDraftChange,
  tags,
  setTags,
  allTags,
  isTagsLoading,
  productionOrganizations,
  elasticCluster,
  isSandbox,
  disabled,
  canDelete,
  onOpenPricingTier,
  onOpenHubspotData,
  onOpenSecureProxyWhitelist,
  onFactoryReset,
  onDelete
}: SettingsTabProps) => {
  const sections = SECTIONS.filter(
    (section) => section.id !== 'danger-zone' || !disabled
  )

  const scrollTo = (id: string) =>
    document
      .getElementById(sectionId(id))
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })

  return (
    <div className="flex flex-wrap items-start gap-8">
      <nav
        aria-label="Settings sections"
        className="flex w-full flex-row flex-wrap gap-1 lg:w-48 lg:flex-col"
      >
        {sections.map((section) => (
          <button
            className={cn(
              'rounded-md px-3 py-2 text-left text-sm text-muted-foreground hover:bg-muted hover:text-foreground',
              section.id === 'danger-zone' && 'text-red-600 dark:text-red-400'
            )}
            key={section.id}
            onClick={() => scrollTo(section.id)}
            type="button"
          >
            {section.label}
          </button>
        ))}
      </nav>

      <div className="min-w-0 flex-1 basis-[36rem] space-y-5">
        <DetailSection
          description="How the organization is identified in the admin portal and in billing."
          id={sectionId('general')}
          title="General"
        >
          <div className="grid items-start gap-5 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                disabled={disabled}
                id="name"
                onChange={(e) => onDraftChange({ name: e.target.value })}
                value={draft.name ?? ''}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                disabled={disabled}
                id="email"
                onChange={(e) => onDraftChange({ email: e.target.value })}
                type="email"
                value={draft.email ?? ''}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="customer_number">Customer number</Label>
              <Input
                disabled={disabled}
                id="customer_number"
                onChange={(e) =>
                  onDraftChange({ customer_number: e.target.value })
                }
                value={draft.customer_number ?? ''}
              />
            </div>
            <fieldset
              aria-labelledby="severity_group_label"
              className="space-y-2"
              disabled={disabled}
            >
              <span
                className="block text-sm font-medium"
                id="severity_group_label"
              >
                Severity group
              </span>
              <Segmented
                ariaLabel="Severity group"
                onChange={(value) => onDraftChange({ severity_group: value })}
                options={toOptions(['Low', 'Medium', 'High'])}
                value={draft.severity_group ?? ''}
              />
            </fieldset>
          </div>
        </DetailSection>

        <DetailSection
          description="Production orgs serve real customers. Sandboxes belong to a production org and can be factory reset."
          id={sectionId('environment')}
          title="Environment"
        >
          <div className="space-y-5">
            <fieldset
              aria-labelledby="production_vs_sandbox_label"
              className="space-y-2"
              disabled={disabled}
            >
              <span
                className="block text-sm font-medium"
                id="production_vs_sandbox_label"
              >
                Production vs. sandbox
              </span>
              <div className="grid gap-3 sm:grid-cols-2">
                {[
                  {
                    value: 'Production',
                    hint: 'Live organization with real customer data'
                  },
                  {
                    value: 'Sandbox',
                    hint: 'Test organization linked to a production org'
                  }
                ].map((option) => (
                  <label
                    className={cn(
                      'grid cursor-pointer grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 rounded-lg border p-4',
                      draft.organization_use === option.value &&
                        'border-primary bg-muted/40'
                    )}
                    htmlFor={`organization_use_${option.value}`}
                    key={option.value}
                  >
                    <input
                      checked={draft.organization_use === option.value}
                      className="row-span-2 mt-0.5"
                      id={`organization_use_${option.value}`}
                      name="organization_use"
                      onChange={() =>
                        onDraftChange({
                          organization_use: option.value as
                            | 'Production'
                            | 'Sandbox',
                          parent_production_org_id:
                            option.value === 'Production'
                              ? undefined
                              : draft.parent_production_org_id
                        })
                      }
                      type="radio"
                      value={option.value}
                    />
                    <span className="text-sm font-medium">{option.value}</span>
                    <span className="text-xs text-muted-foreground">
                      {option.hint}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
            {draft.organization_use === 'Sandbox' && (
              <ParentOrganizationSelect
                disabled={disabled}
                onChange={(id) =>
                  onDraftChange({ parent_production_org_id: id })
                }
                organizations={productionOrganizations}
                value={draft.parent_production_org_id}
              />
            )}
          </div>
        </DetailSection>

        <DetailSection
          description="Used to filter and find organizations in the admin portal."
          id={sectionId('tags')}
          title="Tags"
        >
          <div className="max-w-xl">
            <MultiSelectAutoComplete
              allowNewValues
              disabled={disabled}
              isLoading={isTagsLoading}
              items={allTags || []}
              onNewItem={(item) => item}
              onSelect={(item) => setTags((prev) => [...prev, item as string])}
              onUnselect={(item) =>
                setTags((prev) =>
                  prev.filter((selected) => selected !== (item as string))
                )
              }
              placeholder="Choose the tags to assign to the organization"
              selectedItems={tags}
            />
          </div>
        </DetailSection>

        <DetailSection
          description={
            <>
              Plan, limits and feature overrides are in the{' '}
              <button
                className="text-foreground underline underline-offset-4"
                onClick={onOpenPricingTier}
                type="button"
              >
                Pricing tier
              </button>{' '}
              tab.
            </>
          }
          id={sectionId('subscription')}
          title="Subscription"
        >
          <div className="grid items-start gap-5 md:grid-cols-2">
            <fieldset
              aria-labelledby="subscription_status_label"
              className="space-y-2"
              disabled={disabled}
            >
              <span
                className="block text-sm font-medium"
                id="subscription_status_label"
              >
                Status
              </span>
              <Segmented
                ariaLabel="Subscription status"
                onChange={(value) =>
                  onDraftChange({
                    subscription_status: value,
                    subscription_expiry_date:
                      value === 'Active' ? '' : draft.subscription_expiry_date
                  })
                }
                options={toOptions(['Active', 'Churning'])}
                value={draft.subscription_status ?? ''}
              />
            </fieldset>
            <div className="space-y-2">
              <Label htmlFor="subscription_expiry_date">
                Subscription end date
              </Label>
              <Input
                className="[&::-webkit-calendar-picker-indicator]:dark:invert"
                disabled={disabled || draft.subscription_status !== 'Churning'}
                id="subscription_expiry_date"
                onChange={(e) =>
                  onDraftChange({ subscription_expiry_date: e.target.value })
                }
                type="date"
                value={draft.subscription_expiry_date ?? ''}
              />
              <p className="text-xs text-muted-foreground">
                Available when the status is Churning.
              </p>
            </div>
          </div>
        </DetailSection>

        <DetailSection
          actions={
            <Button
              className="h-auto p-0"
              onClick={onOpenHubspotData}
              variant="link"
            >
              View HubSpot data
            </Button>
          }
          description="The linked company is the source for company size, segments and other CRM data."
          id={sectionId('hubspot')}
          title="HubSpot"
        >
          <div className="max-w-xl">
            <OrganizationHubspotCompany
              collapsible={false}
              disabled={disabled}
              onCompanySelect={(companyId) =>
                onDraftChange({ hubspot_company_id: companyId })
              }
              selectedCompanyId={draft.hubspot_company_id}
            />
          </div>
        </DetailSection>

        <DetailSection
          actions={
            <Button onClick={onOpenSecureProxyWhitelist} variant="outline">
              Manage whitelist
            </Button>
          }
          description="Domains and IP ranges integrations may call through the secure proxy. Changes are reviewed before they apply and are audited."
          id={sectionId('secure-proxy')}
          title="Secure proxy"
        />

        <DetailSection
          actions={
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
              Read-only
            </span>
          }
          bodyClassName="px-5 pb-3 pt-1"
          description="Set by the platform."
          id={sectionId('system')}
          title="System"
        >
          <DefinitionList
            columns={3}
            items={[
              { label: 'ID', value: organization?.id, mono: true },
              { label: 'Symbol', value: organization?.symbol },
              { label: 'Type', value: organization?.type },
              { label: 'Status', value: organization?.status },
              {
                label: 'Created',
                value: organization?.created_date
                  ? `${formatDate(organization.created_date)}, ${new Date(
                      organization.created_date
                    ).toLocaleTimeString('en-GB', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}`
                  : undefined
              },
              {
                label: 'Elastic Search cluster',
                value: elasticCluster,
                mono: true
              }
            ]}
          />
        </DetailSection>

        {!disabled && (
          <DetailSection
            bodyClassName="divide-y py-0"
            description="Each action asks you to confirm in a dialog first."
            id={sectionId('danger-zone')}
            title="Danger zone"
            tone="destructive"
          >
            {isSandbox && (
              <DangerRow
                action="Factory reset"
                description="Takes a snapshot, wipes all entities, configs and blueprint records, then reinstalls base-360. Users, role assignments and snapshots stay."
                disabled={!canDelete}
                onClick={onFactoryReset}
                title="Factory reset sandbox"
              />
            )}
            <DangerRow
              action="Delete organization"
              description="Marks the organization for cleanup. It becomes read-only until cleanup runs, and the deletion can be cancelled until then."
              disabled={!canDelete}
              onClick={onDelete}
              solid
              title="Delete organization"
            />
          </DetailSection>
        )}
      </div>
    </div>
  )
}

const DangerRow = ({
  title,
  description,
  action,
  disabled,
  solid,
  onClick
}: {
  title: string
  description: string
  action: string
  disabled: boolean
  solid?: boolean
  onClick: () => void
}) => (
  <div className="flex flex-wrap items-center justify-between gap-4 py-4">
    <div className="min-w-0 max-w-2xl flex-1 space-y-1">
      <p className="text-sm font-medium">{title}</p>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
    <Button
      className={cn(
        !solid && 'border-red-500/60 text-red-600 dark:text-red-400'
      )}
      disabled={disabled}
      onClick={onClick}
      variant={solid ? 'destructive' : 'outline'}
    >
      {action}
    </Button>
  </div>
)

const ParentOrganizationSelect = ({
  value,
  onChange,
  organizations,
  disabled
}: {
  value?: string
  onChange: (id: string) => void
  organizations?: IOrganization[]
  disabled: boolean
}) => {
  const [open, setOpen] = useState(false)
  const selected = organizations?.find((org) => org.id === value)

  return (
    <div className="max-w-xl space-y-2">
      <Label>Parent production organization</Label>
      <Popover onOpenChange={setOpen} open={open}>
        <PopoverTrigger asChild>
          <Button
            aria-expanded={open}
            className={classNames('w-full justify-between', {
              'text-primary': value,
              'text-placeholder': !value
            })}
            disabled={disabled}
            role="combobox"
            variant="outline"
          >
            {value
              ? selected?.name ?? value
              : 'Select parent production organization'}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[var(--radix-popover-trigger-width)] p-0"
        >
          <Command>
            <CommandInput
              className="h-9"
              placeholder="Search organizations..."
            />
            <CommandList>
              <CommandEmpty>No organization found.</CommandEmpty>
              <CommandGroup>
                {organizations
                  ?.filter((org) => org.organization_use !== 'Sandbox')
                  .map((org) => (
                    <CommandItem
                      className="flex items-center justify-between px-4 py-2"
                      key={org.id}
                      onSelect={() => {
                        onChange(org.id!)
                        setOpen(false)
                      }}
                      value={`${org.name ?? ''} ${org.id ?? ''}`}
                    >
                      <div className="flex items-center gap-2">
                        <Check
                          className={classNames(
                            'mr-2 h-4 w-4',
                            value === org.id ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                        <div className="flex flex-col">
                          <span className="font-medium">{org.name ?? ''}</span>
                          <span className="text-xs text-muted-foreground">
                            ID: {org.id ?? ''}
                          </span>
                        </div>
                      </div>
                    </CommandItem>
                  ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <p className="text-xs text-muted-foreground">
        Search by name or ID. Only production organizations are listed.
      </p>
    </div>
  )
}
